import { SearchTypes } from '@rolebase/shared/model/search'
import { SearchPerimeterProps } from './circlePerimeter'
import incrementalSearch from './incrementalSearch'
import { SearchItem } from './searchTypes'

// Non-selectable id for the separator row inserted between in-perimeter and
// out-of-perimeter results (ORG-02 soft partition). Rendered as a disabled
// label row by SearchResultsList.
const perimeterSeparatorId = '__perimeter_separator__'

export interface FilterSearchItemsOptions {
  defaultEmpty: boolean
  additionalTopItems?: SearchItem[]
  // Label of the "Create …" row; its presence turns the row on.
  createLabel?(name: string): string
  // ORG-02: optional soft-partition perimeter, opt-in only for the circle
  // picker. When omitted, the default behavior applies (members/threads/
  // base-roles searches).
  perimeter?: SearchPerimeterProps
}

function sortByTitle(searchItems: SearchItem[]): SearchItem[] {
  return [...searchItems].sort((a, b) => a.title.localeCompare(b.title))
}

// Never mutates `items` (#153): it is the caller's memoized list. Sorting it
// or unshifting the top items into it in place made them pile up at each
// recompute — "Select in the org chart" showed twice in the meeting's tension
// form, the second one sorted among the roles.
export function filterSearchItems(
  items: SearchItem[],
  searchText: string,
  {
    defaultEmpty,
    additionalTopItems,
    createLabel,
    perimeter,
  }: FilterSearchItemsOptions
): SearchItem[] {
  let searchItems: SearchItem[]

  if (perimeter) {
    // ORG-02: soft partition. Empty field -> perimeter-only (no separator,
    // unless perimeter is "all" which is a flat, unrestricted list). While
    // typing -> in-perimeter items first, then a separator, then the rest
    // (still flat for "all", since isPartitioned is false in that case).
    if (searchText === '') {
      searchItems = defaultEmpty
        ? []
        : perimeter.isPartitioned
          ? sortByTitle(items.filter(perimeter.isInPerimeter))
          : sortByTitle(items)
    } else {
      const matched = incrementalSearch(items, searchText)
      if (perimeter.isPartitioned) {
        const inPerimeter = sortByTitle(matched.filter(perimeter.isInPerimeter))
        const outPerimeter = sortByTitle(
          matched.filter((item) => !perimeter.isInPerimeter(item))
        )
        searchItems =
          outPerimeter.length > 0
            ? [
                ...inPerimeter,
                {
                  id: perimeterSeparatorId,
                  type: SearchTypes.Circle,
                  text: '',
                  title: perimeter.separatorLabel,
                  isSeparator: true,
                },
                ...outPerimeter,
              ]
            : inPerimeter
      } else {
        searchItems = sortByTitle(matched)
      }
    }
  } else {
    // Default behavior, unchanged from before ORG-02.
    searchItems =
      searchText === ''
        ? defaultEmpty
          ? []
          : items
        : incrementalSearch(items, searchText)

    searchItems = sortByTitle(searchItems)
  }

  // Add Create item
  if (createLabel && searchText.length >= 1) {
    return searchItems.concat({
      id: 'create',
      text: searchText,
      type: SearchTypes.CreateAction,
      title: createLabel(searchText),
    })
  }

  if (additionalTopItems && searchText === '') {
    return [...additionalTopItems, ...searchItems]
  }

  return searchItems
}
